import { useCallback, useEffect, useState } from 'react';
import { Button, Text } from 'rizzui';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import CustomTable from '../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { NetworkService } from '../../../services/network/networkService';
import { fixNumber } from '../../../utils/formatNumber';
import NetworkSettlementsFilters from './NetworkSettlementsFilters';

const columnOrder = [
    'commercialNetworkName',
    'contractsCount',
    'totalCommission',
    'adjustments',
    'netAmount',
];

const NetworkSettlementsList = () => {
    const service = new NetworkService();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const navigate = useNavigate();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);
    const [selectedSettlementIds, setSelectedSettlementIds] = useState<string[]>([]);

    const handleSelectedRowsChange = useCallback((selectedRows: string[]) => {
        setSelectedSettlementIds((prev) => {
            const prevKey = prev.join('|');
            const nextKey = selectedRows.join('|');
            return prevKey === nextKey ? prev : selectedRows;
        });
    }, []);

    useEffect(() => {
        setSelectedSettlementIds([]);
    }, [filters.filter_filters?.month, filters.filter_filters?.year, filters.filter_filters?.settlement_status, filters.filter_filters?.commercial_network]);

    const [data, loading] = useFetch(useCallback(async () => {
        const response = await service.listNetworkSettlements(filters);
        return response.getResponseData();
    }, [filters]));

    const orderedData = () => data?.data?.map((row: any) => {
        const orderedRow: any = { id: row.id };
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const exportFilters: any = {
                ...filters,
                filter_filters: {
                    ...(filters.filter_filters ?? {}),
                },
            };

            if (selectedSettlementIds.length > 0) {
                exportFilters.filter_filters.commercial_network = selectedSettlementIds;
            }

            const response = await service.exportNetworkSettlements(exportFilters);
            if (!response) {
                toast.error('No se pudo exportar el fichero de liquidaciones');
                return;
            }

            const fileData = response.getResponseData();
            const blob = new Blob([fileData]);
            const url = window.URL.createObjectURL(blob);
            const selectedMonth = Number(filters.filter_filters?.month ?? (new Date().getMonth() + 1));
            const selectedYear = Number(filters.filter_filters?.year ?? new Date().getFullYear());

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `liquidaciones_red_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (requestError: any) {
            const responseData = requestError?.response?.data;
            if (responseData?.message) {
                toast.error(responseData.message);
            } else {
                handleErrors(responseData ?? requestError);
            }
        } finally {
            setIsExporting(false);
        }
    };

    return (
            <Page container='fluid'>
            {data && (
                <CustomTable
                    largeTable
                    id={'network-settlements-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    overrideColumns={[
                        {
                            key: 'commercialNetworkName',
                            columnNameKey: 'red comercial',
                            render: (row: any) => (
                                <button
                                    type="button"
                                    className="text-secondary font-medium hover:underline"
                                    onClick={() => {
                                        const networkId = row.row.original.id;
                                        const month = Number(filters.filter_filters?.month ?? (new Date().getMonth() + 1));
                                        const year = Number(filters.filter_filters?.year ?? new Date().getFullYear());
                                        const status = filters.filter_filters?.settlement_status;

                                        const params = new URLSearchParams();
                                        params.set('month', String(month));
                                        params.set('year', String(year));
                                        if (status) {
                                            params.set('settlement_status', String(status));
                                        }

                                        navigate(
                                            `${menuRoutes.networks.path}${menuRoutes.networks.settlements}/${networkId}?${params.toString()}`,
                                            { state: { commercialNetworkName: row.row.original.commercialNetworkName } }
                                        );
                                    }}
                                >
                                    {row.row.original.commercialNetworkName}
                                </button>
                            )
                        },
                        {
                            key: 'contractsCount',
                            columnNameKey: 'contratos',
                            render: (row: any) => <Text>{Number(row.row.original.contractsCount) || 0}</Text>
                        },
                        {
                            key: 'totalCommission',
                            render: (row: any) => <Text>{fixNumber(Number(row.row.original.totalCommission), 2)} €</Text>
                        },
                        {
                            key: 'adjustments',
                            render: (row: any) => (
                                <Text className={Number(row.row.original.adjustments) < 0 ? 'text-red-600 font-medium' : ''}>
                                    {fixNumber(Number(row.row.original.adjustments), 2)} €
                                </Text>
                            )
                        },
                        {
                            key: 'netAmount',
                            columnNameKey: 'neto',
                            render: (row: any) => (
                                <Text className={Number(row.row.original.netAmount) < 0 ? 'text-red-600 font-medium' : 'font-medium'}>
                                    {fixNumber(Number(row.row.original.netAmount), 2)} €
                                </Text>
                            )
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage || 1,
                        totalCount: data?.totalRegisters || 0,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                    enableRowSelection
                    onSelectedRowsChange={handleSelectedRowsChange}
                >
                    <Button
                        variant={'solid'}
                        color={'secondary'}
                        size={'sm'}
                        onClick={handleExport}
                        className='p-3 text-nowrap'
                        disabled={isExporting}
                        isLoading={isExporting}
                    >
                        Exportar Excel
                    </Button>
                </CustomTable>
            )}

            {!data && loading && (<Loader height='40vh' />)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Liquidaciones de Red'}>
                <NetworkSettlementsFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    resetFilters={resetFilters}
                />
            </FilterDrawerView>
        </Page>
    );
};

export default NetworkSettlementsList;
